// src/pages/teacher/TeacherDashboard.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Container, Row, Col, Card, Button, Table, Spinner, Alert, ListGroup, Form,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../services/firebase";
import {
  doc, getDoc, collection, query, where, orderBy, onSnapshot, limit, getDocs,
} from "firebase/firestore";

// 3D icons (adjust paths to your assets)
import Dash3D from "../../assets/3d/dashboard.png";
import Attend3D from "../../assets/3d/attendance.png";
import Exams3D from "../../assets/3d/exams.png";
import Grade3D from "../../assets/3d/grade.png";
import Roster3D from "../../assets/3d/roster.png";
import Live3D from "../../assets/3d/online-class.png";
import Megaphone3D from "../../assets/3d/megaphone.png";


const ICONS_3D = {
  dashboard: Dash3D,
  attendance: Attend3D,
  exams: Exams3D,
  upload: Grade3D,
  roster: Roster3D,
  live: Live3D,
  megaphone: Megaphone3D,
};

/* ---------------- helpers ---------------- */
// normalize teacher sections (accept array | csv | single)
const parseSections = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map(String).map((s) => s.trim()).filter(Boolean).map((s) => s.toUpperCase());
  }
  return String(raw)
    .split(/[,\s/|]+/g)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => s.toUpperCase());
};

const upper = (s) => String(s || "").trim().toUpperCase();
const lower = (s) => String(s || "").trim().toLowerCase();
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmt = (d, f = "DD MMM • HH:mm") =>
  (d && dayjs(d).isValid() ? dayjs(d).format(f) : "—");

const withinNextDays = (dateLike, n = 7) => {
  if (!dateLike) return false;
  const d = dayjs(toDate(dateLike));
  const now = dayjs().startOf("day");
  return d.isSame(now, "day") || (d.isAfter(now) && d.isBefore(now.add(n, "day")));
};

// exam target check for a class + ANY of teacher’s sections
function examTargetsTeacher(exam, className, teacherSectionsU = []) {
  const hasSectionFilter = teacherSectionsU.length > 0;
  const secSet = new Set(teacherSectionsU); // UPPERCASE
  // v3: targets: [{ classId, sectionKey? }]
  if (Array.isArray(exam.targets) && exam.targets.length) {
    return exam.targets.some((t) => {
      if (String(t.classId) !== String(className)) return false;
      const secU = upper(t.sectionKey || "");
      if (!hasSectionFilter) return true; // teacher sees all sections for that class
      return secU === "" || secSet.has(secU); // blank = all sections
    });
  }
  // v2: section map { [classId]: ["A","B",""] }
  if (exam.sectionKeysByClass && exam.sectionKeysByClass[className]) {
    const arrU = (exam.sectionKeysByClass[className] || []).map(upper);
    if (!hasSectionFilter) return true;
    return arrU.includes("") || arrU.some((sec) => secSet.has(sec));
  }
  // v1: only classIds
  if (Array.isArray(exam.classIds) && exam.classIds.includes(className)) return true;
  return false;
}

/* ---------- motion variants ---------- */
const reveal = {
  hidden: { opacity: 0, y: 8 },
  show:  { opacity: 1, y: 0, transition: { duration: .28, ease: "easeOut" } },
};
const stagger = {
  show: { transition: { staggerChildren: .06, delayChildren: .04 } }
};
const hoverCard = {
  rest:  { y: 0, boxShadow: "0 0 0 rgba(0,0,0,0)" },
  hover: { y: -2, boxShadow: "0 12px 24px rgba(0,0,0,.12)" }
};

/* ---------- tiny UI helpers ---------- */
function EmptyState({ img, title, subtitle, cta, onClick }) {
  return (
    <div className="empty-state">
      {img ? <img src={img} alt="" /> : <i className="bi bi-inboxes" />}
      <div className="title">{title}</div>
      {subtitle ? <div className="sub">{subtitle}</div> : null}
      {cta ? <Button size="sm" className="btn-accent mt-2" onClick={onClick}>{cta}</Button> : null}
    </div>
  );
}
function SkeletonLines({ rows = 4 }) {
  return (
    <div className="skeleton-block">
      {Array.from({ length: rows }).map((_, i) => <div key={i} className="sk-line" />)}
    </div>
  );
}

export default function TeacherDashboard() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const schoolId = profile?.schoolId || null;
  const teacherIdPref = profile?.teacherId || null;

  const [teacher, setTeacher] = useState(null);
  const isInactive = useMemo(
    () => String(teacher?.status || "").toLowerCase() === "inactive",
    [teacher]
  );
  const [loadingTeacher, setLoadingTeacher] = useState(true);
  const [teacherErr, setTeacherErr] = useState("");

  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);

  const [exams, setExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(true);

  // KPIs
  const [todayAttendanceTaken, setTodayAttendanceTaken] = useState(false);
  const [upcomingCount, setUpcomingCount] = useState(0);
  const [pendingGrading, setPendingGrading] = useState(0);

  // Feeds
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(true);
  const [circulars, setCirculars] = useState([]);
  const [loadingCirculars, setLoadingCirculars] = useState(true);

  // roster search
  const [rosterQuery, setRosterQuery] = useState("");

  // Cancel guard
  const aliveRef = useRef(true);
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);

  /* -------- load teacher doc (prefer profile.teacherId; fallback by email) -------- */
  useEffect(() => {
    if (!schoolId || !user?.uid) {
      setTeacher(null);
      setLoadingTeacher(false);
      return;
    }
    setLoadingTeacher(true);
    setTeacherErr("");

    (async () => {
      try {
        // 1) prefer explicit teacherId
        if (teacherIdPref) {
          const ref = doc(db, "schools", schoolId, "teachers", teacherIdPref);
          const snap = await getDoc(ref);
          if (!aliveRef.current) return;
          if (snap.exists()) {
            setTeacher({ id: snap.id, ...(snap.data() || {}) });
            setLoadingTeacher(false);
            return;
          }
        }
        // 2) fallback by email
        const email = lower(user?.email || "");
        if (email) {
          const qy = query(
            collection(db, "schools", schoolId, "teachers"),
            where("email", "==", email),
            limit(1)
          );
          const qs = await getDocs(qy);
          if (!aliveRef.current) return;
          if (!qs.empty) {
            const d = qs.docs[0];
            setTeacher({ id: d.id, ...(d.data() || {}) });
            setLoadingTeacher(false);
            return;
          }
        }
        if (aliveRef.current) {
          setTeacher(null);
          setTeacherErr("Teacher record not found for your account.");
          setLoadingTeacher(false);
        }
      } catch {
        if (!aliveRef.current) return;
        setTeacher(null);
        setTeacherErr("Failed to load teacher profile.");
        setLoadingTeacher(false);
      }
    })();
  }, [schoolId, teacherIdPref, user?.email, user?.uid]);

  const teacherName =
    teacher?.name || profile?.displayName || user?.displayName || user?.email || "Teacher";

  // teacher’s mapped classes & sections
  const teacherClasses = useMemo(
    () =>
      Array.isArray(teacher?.classIds) && teacher.classIds.length
        ? teacher.classIds.filter(Boolean)
        : teacher?.className
        ? [teacher.className]
        : [],
    [teacher]
  );
  const teacherSectionsU = useMemo(
    () => parseSections(teacher?.sectionIds ?? teacher?.sections ?? teacher?.section),
    [teacher]
  );

  // Which class/section are we viewing?
  const [activeClass, setActiveClass] = useState("");
  const [activeSection, setActiveSection] = useState(""); // "" = All sections
  useEffect(() => {
    if (!teacherClasses.length) { setActiveClass(""); return; }
    setActiveClass((prev) => (teacherClasses.includes(prev) ? prev : teacherClasses[0]));
  }, [teacherClasses]);

  // Reset/keep section on class change
  useEffect(() => { setActiveSection(""); }, [activeClass]);
  // computed roster list with search
  const filteredStudents = useMemo(() => {
    const q = rosterQuery.trim().toLowerCase();
    if (!q) return students;
    return students.filter(s => {
      const name = (s.name || "").toLowerCase();
      const roll = String(s.rollNo || "").toLowerCase();
      const phone = String(s.phone || "").toLowerCase();
      const email = String(s.email || "").toLowerCase();
      return name.includes(q) || roll.includes(q) || phone.includes(q) || email.includes(q);
    });
  }, [students, rosterQuery]);
  // Section options: if teacher has specific section mapping, use that; else derive from roster.
  const sectionOptions = useMemo(() => {
    if (teacherSectionsU.length) return ["", ...teacherSectionsU];
    const uniq = Array.from(new Set(students.map((s) => upper(s.section || "")).filter(Boolean))).sort();
    return ["", ...uniq];
  }, [teacherSectionsU.join("|"), students]);

  const classTag = useMemo(() => {
    if (!activeClass) return "—";
    const sec =
      activeSection === ""
        ? teacherSectionsU.length
          ? `-${teacherSectionsU.join(",")}`
          : ""
        : `-${activeSection}`;
    return `${activeClass}${sec}`;
  }, [activeClass, activeSection, teacherSectionsU]);

  /* -------- class roster -------- */
  useEffect(() => {
    if (isInactive || !schoolId || !activeClass) {
      setStudents([]); setLoadingStudents(false); return;
    }
    setLoadingStudents(true);
    const qy = query(
      collection(db, "schools", schoolId, "students"),
      where("className", "==", activeClass),
      orderBy("name", "asc"),
      limit(400)
    );
    const unsub = onSnapshot(
      qy,
      (snap) => {
        if (!aliveRef.current) return;
        const allowedSet = new Set(teacherSectionsU); // UPPERCASE
        const hasTeacherSectionFilter = allowedSet.size > 0;
        const activeSecU = upper(activeSection); // "" means All
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((s) => {
            const secU = upper(s.section || "");
            if (activeSecU) return secU === activeSecU;
            if (hasTeacherSectionFilter) return secU === "" || allowedSet.has(secU);
            return true;
          });
        setStudents(rows);
        setLoadingStudents(false);
      },
      () => { if (!aliveRef.current) return; setLoadingStudents(false); }
    );
    return () => { try { unsub(); } catch {} };
  }, [schoolId, activeClass, activeSection, teacherSectionsU.join("|"), isInactive]);

  /* -------- exams -------- */
  useEffect(() => {
    if (isInactive || !schoolId || !activeClass) { setExams([]); setLoadingExams(false); return; }
    setLoadingExams(true);
    const qy = query(
      collection(db, "schools", schoolId, "exams"),
      where("classIds", "array-contains", activeClass),
      limit(400)
    );
    const unsub = onSnapshot(
      qy,
      (snap) => {
        if (!aliveRef.current) return;
        const secFilter = activeSection ? [upper(activeSection)] : teacherSectionsU;
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((e) => !!e.isPublished && examTargetsTeacher(e, activeClass, secFilter))
          .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
        setExams(rows);
        setUpcomingCount(rows.filter((e) => withinNextDays(e.date, 7)).length);
        setLoadingExams(false);
      },
      () => { if (!aliveRef.current) return; setLoadingExams(false); }
    );
    return () => { try { unsub(); } catch {} };
  }, [schoolId, activeClass, activeSection, teacherSectionsU.join("|"), isInactive]);

  /* -------- attendance today? -------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isInactive || !schoolId || !activeClass) { setTodayAttendanceTaken(false); return; }
      const start = dayjs().startOf("day").toDate();
      const end = dayjs().endOf("day").toDate();
      const base = collection(db, "schools", schoolId, "attendance");

      const checkFieldAny = async (fieldName) => {
        const qs = await getDocs(
          query(
            base,
            where("className", "==", activeClass),
            where(fieldName, ">=", start),
            where(fieldName, "<=", end),
            limit(100)
          )
        );
        if (cancelled || !aliveRef.current) return false;
        const docs = qs.docs.map((d) => d.data() || []);
        const allowedSet = new Set(
          activeSection
            ? [upper(activeSection), ""]
            : teacherSectionsU.length
            ? [...teacherSectionsU, ""]
            : []
        );
        if (allowedSet.size === 0) return docs.length > 0;
        return docs.some((x) => allowedSet.has(upper(x.section || "")));
      };

      try {
        if (await checkFieldAny("date")) { setTodayAttendanceTaken(true); return; }
        if (await checkFieldAny("updatedAt")) { setTodayAttendanceTaken(true); return; }
        if (await checkFieldAny("createdAt")) { setTodayAttendanceTaken(true); return; }
        setTodayAttendanceTaken(false);
      } catch (e) {
        console.warn("[ATT checker] error:", e);
        if (!cancelled) setTodayAttendanceTaken(false);
      }
    })();
    return () => { cancelled = true; };
  }, [schoolId, activeClass, activeSection, teacherSectionsU.join("|"), isInactive]);

  /* -------- pending grading (descriptive exams only) -------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isInactive || !schoolId || exams.length === 0) { setPendingGrading(0); return; }
      const desc = exams.filter((e) => String(e.examType || "").toLowerCase() === "descriptive");
      const head = desc.slice(0, 10);
      const jobs = head.map((ex) => {
        const attemptsRef = collection(db, "schools", schoolId, "exams", ex.id, "attempts");
        return getDocs(query(attemptsRef, where("status", "==", "submitted"), limit(300)))
          .then((qs) => qs)
          .catch(() => null);
      });
      const results = await Promise.all(jobs);
      if (cancelled || !aliveRef.current) return;
      let pending = 0;
      results.forEach((qs) => {
        if (!qs) return;
        qs.docs.forEach((d) => {
          const a = d.data() || {};
          const evaluated = a.evaluated === true;
          if (!evaluated) pending += 1;
        });
      });
      setPendingGrading(pending);
    })();
    return () => { cancelled = true; };
  }, [schoolId, exams, isInactive]);

  /* -------- recent submissions feed -------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isInactive || !schoolId || exams.length === 0) { setRecentSubmissions([]); setLoadingSubs(false); return; }
      setLoadingSubs(true);
      const head = exams.slice(0, 8);
      const jobs = head.map((ex) => {
        const attemptsRef = collection(db, "schools", schoolId, "exams", ex.id, "attempts");
        return getDocs(query(attemptsRef, orderBy("updatedAt", "desc"), limit(12)))
          .then((qs) => ({ ex, qs }))
          .catch(() => ({ ex, qs: null }));
      });
      const results = await Promise.all(jobs);
      if (cancelled || !aliveRef.current) return;

      const buckets = [];
      results.forEach(({ ex, qs }) => {
        if (!qs) return;
        qs.docs.forEach((d) => {
          const a = { id: d.id, ...(d.data() || {}) };
          buckets.push({
            examId: ex.id,
            examName: ex.name || "Exam",
            subject: ex.subject || "",
            studentName: a.byStudent?.name || "",
            status: a.status || "",
            uploaded: !!a.upload?.url,
            updatedAt: toDate(a.updatedAt),
          });
        });
      });

      buckets.sort((a, b) => (b.updatedAt?.getTime?.() || 0) - (a.updatedAt?.getTime?.() || 0));
      setRecentSubmissions(buckets.slice(0, 40));
      setLoadingSubs(false);
    })();
    return () => { cancelled = true; };
  }, [schoolId, exams, isInactive]);

  /* -------- latest circulars -------- */
  useEffect(() => {
    if (isInactive || !schoolId) { setCirculars([]); setLoadingCirculars(false); return; }
    setLoadingCirculars(true);
    const qy = query(
      collection(db, "schools", schoolId, "circulars"),
      orderBy("createdAt", "desc"),
      limit(40)
    );
    const unsub = onSnapshot(
      qy,
      (snap) => { if (!aliveRef.current) return; setCirculars(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))); setLoadingCirculars(false); },
      () => { if (!aliveRef.current) return; setLoadingCirculars(false); }
    );
    return () => unsub();
  }, [schoolId, isInactive]);

  /* -------- roster export (xlsx) -------- */
  const exportRoster = () => {
    const data = students.map((s) => ({
      Name: s.name || "", Roll: s.rollNo || "", Phone: s.phone || "", Email: s.email || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Roster");
    const secForName = activeSection ? `_${activeSection}` : "";
    XLSX.writeFile(wb, `roster_${activeClass || "class"}${secForName}.xlsx`);
  };

  /* -------- action items -------- */
  const actionItems = useMemo(() => {
    const items = [];
    if (!todayAttendanceTaken && activeClass) {
      items.push({
        key: "att",
        label: "Today's attendance not taken",
        action: () => navigate("/teacher/attendance"),
        icon: "bi-clipboard-check",
        variant: "warning",
      });
    }
    if (pendingGrading > 0) {
      items.push({
        key: "grading",
        label: `${pendingGrading} descriptive submissions pending review`,
        action: () => navigate("/teacher/exams"),
        icon: "bi-pen",
        variant: "info",
      });
    }
    if (items.length === 0) {
      items.push({
        key: "allgood",
        label: "All caught up. Nice work!",
        action: null,
        icon: "bi-emoji-smile",
        variant: "success",
      });
    }
    return items;
  }, [todayAttendanceTaken, pendingGrading, activeClass, navigate]);

  if (!loadingTeacher && teacher && isInactive) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger" className="mb-3">
          <div className="fw-semibold mb-1">Access restricted</div>
          Your teacher account is marked as <b>Inactive</b>. Please contact the administrator to regain access.
        </Alert>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={() => navigate("/")}>Go Home</Button>
          <Button variant="outline-secondary" onClick={() => navigate("/support")}>Contact Support</Button>
        </div>
      </Container>
    );
  }



  return (
    <Container fluid className="p-0">
      {/* ===== HERO + QUICK ACTIONS ===== */}
      <motion.div variants={stagger} initial="hidden" animate="show">
        {/* Modern hero */}
        <motion.div className="tdb-hero pro mb-3" variants={reveal}>
          <div className="tdb-hero-left">
            <div className="tdb-eyebrow">Teacher Dashboard</div>
            <h4 className="mb-1 fw-bold">Hello, {String(teacherName).split(" ")[0]} 👋</h4>

            {/* Context chips */}
            <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
              {teacher?.primarySubject ? (
                <span className="chip-accent">
                  <i className="bi bi-journal-text me-1" />
                  {teacher.primarySubject}
                </span>
              ) : null}
              {classTag !== "—" && <span className="chip-accent">Class {classTag}</span>}
            </div>

            {/* Class & Section selectors */}
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <Form.Select size="sm" value={activeClass} onChange={(e) => setActiveClass(e.target.value)} style={{ minWidth: 140 }}>
                {!teacherClasses.length && <option value="">No class</option>}
                {teacherClasses.map((c) => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
              <Form.Select size="sm" value={activeSection} onChange={(e) => setActiveSection(e.target.value)} style={{ minWidth: 120 }}>
                {sectionOptions.map((s) => s === "" ? <option key="_all" value="">All sections</option> : <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </div>

            {/* Inline KPIs */}
            <div className="hero-kpis">
              <div className="hkpi">
                <div className="hkpi-label">Students</div>
                <div className="hkpi-value">{students.length}</div>
              </div>
              <div className="hkpi">
                <div className="hkpi-label">Attendance</div>
                <div className={`hkpi-badge ${todayAttendanceTaken ? "ok" : "warn"}`}>
                  {todayAttendanceTaken ? "Done" : "Pending"}
                </div>
              </div>
              <div className="hkpi">
                <div className="hkpi-label">Exams (7d)</div>
                <div className="hkpi-value">{upcomingCount}</div>
              </div>
              <div className="hkpi">
                <div className="hkpi-label">To grade</div>
                <div className="hkpi-value">{pendingGrading}</div>
              </div>
            </div>
          </div>

          {/* Right-side 3D art */}
          <div className="tdb-hero-art">
            <img src={ICONS_3D.dashboard} alt="" />
          </div>
        </motion.div>

        {/* Quick actions (3D tiles) */}
        <motion.div className="qa-grid mb-3" variants={reveal}>
          {[
            { key: "att", img: ICONS_3D.attendance, label: "Take Attendance", onClick: () => navigate("/teacher/attendance") },
            { key: "ex",  img: ICONS_3D.exams,      label: "Manage Exams",   onClick: () => navigate("/teacher/exams") },
            { key: "gr",  img: ICONS_3D.upload,     label: "Grade Submissions", onClick: () => navigate("/teacher/exams") },
            { key: "cl",  img: ICONS_3D.live,       label: "Online Class",   onClick: () => navigate("/teacher/onlineclasses") },
            { key: "rs",  img: ICONS_3D.roster,     label: "Export Roster",  onClick: exportRoster },
          ].map(t => (
            <motion.button
              key={t.key}
              className="qa-tile"
              onClick={t.onClick}
              whileHover={{ y: -3, scale: 1.02 }}
              whileTap={{ scale: .98 }}
              transition={{ type: "spring", stiffness: 260, damping: 16 }}
            >
              <img src={t.img} alt="" />
              <div className="qa-label">{t.label}</div>
            </motion.button>
          ))}
        </motion.div>
      </motion.div>

      {/* ===== KPI CARDS ===== */}
      <Row className="g-3 mb-2" as={motion.div} variants={stagger} initial="hidden" animate="show">
        {[
          { label: "Students", value: students.length },
          { label: "Today’s Attendance", value: todayAttendanceTaken ? "Done" : "Pending", cta: !todayAttendanceTaken && { text:"Start", click: () => navigate("/teacher/attendance") }},
          { label: "Upcoming Exams (7d)", value: upcomingCount, cta: { text:"Open", click: () => navigate("/teacher/exams"), outline: true } },
          { label: "Pending Grading", value: pendingGrading, cta: { text:"Grade", click: () => navigate("/teacher/exams"), outline: true } },
        ].map((k, i) => (
          <Col md={3} sm={6} key={i} as={motion.div} variants={reveal}>
            <Card
              className="kpi-card"
              as={motion.div}
              initial="rest"
              whileHover="hover"
              animate="rest"
              variants={hoverCard}
            >
              <Card.Body className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="small text-muted">{k.label}</div>
                  <div className={`fs-4 fw-bold ${k.label.includes("Attendance") ? (todayAttendanceTaken ? "text-success" : "text-warning") : ""}`}>
                    {k.value}
                  </div>
                </div>
                {k.cta ? (
                  <Button
                    size="sm"
                    onClick={k.cta.click}
                    className={k.cta.outline ? "btn-outline-accent" : "btn-accent"}
                    variant={k.cta.outline ? "outline-secondary" : undefined}
                  >
                    {k.cta.text}
                  </Button>
                ) : null}
                <div className="kpi-glow" />
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ===== Action Required + Profile ===== */}
      <Row className="g-3">
        <Col md={4}>
          <Card className="soft-card h-100" as={motion.div} variants={reveal} initial="hidden" animate="show">
            <Card.Header className="bg-transparent fw-semibold">Action Required</Card.Header>
            <Card.Body className="p-0">
              <ListGroup variant="flush" className="scroll-area">
                {actionItems.map((it) => (
                  <ListGroup.Item
                    key={it.key}
                    className="d-flex align-items-center justify-content-between list-soft"
                  >
                    <div className="d-flex align-items-center">
                      <span className={`badge-soft me-2 badge-${it.variant}`}>
                        <i className={`bi ${it.icon}`} />
                      </span>
                      <span>{it.label}</span>
                    </div>
                    {it.action ? (
                      <Button size="sm" variant="outline-secondary" onClick={it.action} className="btn-outline-accent">
                        Open
                      </Button>
                    ) : null}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col md={8}>
          <Card className="soft-card h-100" as={motion.div} variants={reveal} initial="hidden" animate="show">
            <Card.Header className="fw-semibold">Your Profile</Card.Header>
            <Card.Body>
              {loadingTeacher ? (
                <div className="text-muted"><Spinner size="sm" animation="border" /> Loading profile…</div>
              ) : teacherErr ? (
                <Alert variant="warning" className="mb-0">{teacherErr}</Alert>
              ) : !teacher ? (
                <Alert variant="warning" className="mb-0">Teacher record is not linked to this account.</Alert>
              ) : (
                <>
                  <div className="d-flex align-items-center mb-2">
                    {teacher.photoURL ? (
                      <img
                        src={teacher.photoURL}
                        alt={teacherName}
                        style={{
                          height: 56, width: 56, borderRadius: 14, objectFit: "cover", marginRight: 12,
                          border: "1px solid var(--border)",
                        }}
                      />
                    ) : (
                      <div
                        className="border d-inline-flex align-items-center justify-content-center"
                        style={{ height: 56, width: 56, borderRadius: 14, marginRight: 12 }}
                      >
                        <i className="bi bi-person fs-3" />
                      </div>
                    )}
                    <div>
                      <div className="fw-semibold">{teacherName}</div>
                      <div className="text-muted">{teacher.email || user?.email || "—"}</div>
                    </div>
                  </div>
                  <div className="small">
                    <div><span className="text-muted">Employee ID:</span> {teacher.employeeId || "—"}</div>
                    <div><span className="text-muted">Primary Subject:</span> {teacher.primarySubject || "—"}</div>
                    <div><span className="text-muted">Class / Section:</span> {classTag}</div>
                    <div>
                      <span className="text-muted">Status:</span>{" "}
                      <span className={`badge-soft ${teacher.status === "inactive" ? "badge-secondary" : "badge-success"}`}>
                        {teacher.status === "inactive" ? "Inactive" : "Active"}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* ===== Class roster & Upcoming exams ===== */}
      <Row className="g-3 mt-1">
        <Col md={7}>
          <Card className="soft-card" as={motion.div} variants={reveal} initial="hidden" animate="show">
            <Card.Header className="fw-semibold d-flex align-items-center justify-content-between">
              <span>Class Roster {classTag !== "—" ? <span className="ms-2 small text-muted">({classTag})</span> : null}</span>
              <div className="roster-search">
                <i className="bi bi-search" />
                <Form.Control
                  size="sm"
                  value={rosterQuery}
                  onChange={(e) => setRosterQuery(e.target.value)}
                  placeholder="Search name, roll, phone, email…"
                />
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              <AnimatePresence mode="wait">
                {loadingStudents ? (
                  <motion.div key="sk" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-3">
                    <SkeletonLines rows={6} />
                  </motion.div>
                ) : filteredStudents.length === 0 ? (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-3">
                    <EmptyState
                      img={ICONS_3D.roster}
                      title="No students match"
                      subtitle={rosterQuery ? `Try clearing your search.` : `No students found for your selection.`}
                      cta={rosterQuery ? "Clear search" : ""}
                      onClick={() => setRosterQuery("")}
                    />
                  </motion.div>
                ) : (
                  <motion.div key="tbl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="table-responsive scroll-area">
                    <Table hover className="mb-0 align-middle table-soft-hover">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th className="d-none d-md-table-cell">Roll</th>
                          <th className="d-none d-lg-table-cell">Phone</th>
                          <th className="d-none d-lg-table-cell">Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map((s) => (
                          <motion.tr key={s.id} whileHover={{ backgroundColor: "color-mix(in oklab, var(--brand-accent) 9%, transparent)" }}>
                            <td className="fw-semibold text-muted">{s.name || "—"}</td>
                            <td className="d-none d-md-table-cell text-muted">{s.rollNo || "—"}</td>
                            <td className="d-none d-lg-table-cell text-muted">{s.phone || "—"}</td>
                            <td className="d-none d-lg-table-cell text-muted">{s.email || "—"}</td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </Table>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card.Body>
          </Card>
        </Col>

        <Col md={5}>
          <Card className="soft-card" as={motion.div} variants={reveal} initial="hidden" animate="show">
            <Card.Header className="bg-transparent fw-semibold">Upcoming / Live Exams</Card.Header>
            <Card.Body className="p-0">
              <AnimatePresence mode="wait">
                {loadingExams ? (
                  <motion.div key="sk" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-3">
                    <SkeletonLines rows={6} />
                  </motion.div>
                ) : exams.length === 0 ? (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-3">
                    <EmptyState img={ICONS_3D.exams} title="No scheduled exams" subtitle="New exams will appear here." />
                  </motion.div>
                ) : (
                  <motion.div key="tbl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="table-responsive scroll-area">
                    <Table hover className="mb-0 align-middle table-soft-hover">
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th className="d-none d-xl-table-cell">Subject</th>
                          <th>Date</th>
                          <th className="d-none d-xl-table-cell">Time</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exams.map((e) => (
                          <tr key={e.id}>
                            <td className="fw-semibold text-muted">{e.name || "Exam"}</td>
                            <td className="d-none d-xl-table-cell text-muted">{e.subject || "—"}</td>
                            <td className="fw-semibold text-muted">
                              {e.date ? dayjs(toDate(e.date)).format("DD MMM YYYY") : "—"}
                            </td>
                            <td className="d-none d-xl-table-cell text-muted">
                              {e.startTime && e.endTime ? `${e.startTime}–${e.endTime}` : "—"}
                            </td>
                            <td>
                              <span
                                className={`badge-soft ${
                                  e.status === "published"
                                    ? "badge-success"
                                    : e.status === "scheduled"
                                    ? "badge-info"
                                    : "badge-secondary"
                                }`}
                              >
                                {e.status || "—"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ===== Feeds: Recent submissions & Latest circulars ===== */}
      <Row className="g-3 mt-1">
        <Col md={7}>
          <Card className="soft-card" as={motion.div} variants={reveal} initial="hidden" animate="show">
            <Card.Header className="bg-transparent fw-semibold">Recent Submissions</Card.Header>
            <Card.Body className="p-0">
              <AnimatePresence mode="wait">
                {loadingSubs ? (
                  <motion.div key="sk" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-3">
                    <SkeletonLines rows={6} />
                  </motion.div>
                ) : recentSubmissions.length === 0 ? (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-3">
                    <EmptyState img={ICONS_3D.upload} title="No recent submissions" subtitle="New updates will show up here." />
                  </motion.div>
                ) : (
                  <motion.div key="tbl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="table-responsive scroll-area">
                    <Table hover className="mb-0 align-middle table-soft-hover">
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th className="d-none d-lg-table-cell">Exam</th>
                          <th className="d-none d-lg-table-cell">Subject</th>
                          <th>Status</th>
                          <th>Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentSubmissions.map((r, i) => (
                          <tr key={`${r.examId}-${i}`}>
                            <td className="text-muted">{r.studentName || "—"}</td>
                            <td className="d-none d-lg-table-cell text-muted">{r.examName}</td>
                            <td className="d-none d-lg-table-cell text-muted">{r.subject || "—"}</td>
                            <td>
                              <span
                                className={`badge-soft ${
                                  r.status === "submitted"
                                    ? "badge-secondary"
                                    : r.status === "in-progress"
                                    ? "badge-warning"
                                    : "badge-light"
                                }`}
                              >
                                {r.status || "—"}
                              </span>
                            </td>
                            <td className="text-muted">{fmt(r.updatedAt, "DD MMM • HH:mm")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card.Body>
          </Card>
        </Col>

        <Col md={5}>
          <Card className="soft-card" as={motion.div} variants={reveal} initial="hidden" animate="show">
            <Card.Header className="bg-transparent fw-semibold">Latest Circulars</Card.Header>
            <Card.Body className="p-0">
              <AnimatePresence mode="wait">
                {loadingCirculars ? (
                  <motion.div key="sk" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-3">
                    <SkeletonLines rows={5} />
                  </motion.div>
                ) : circulars.length === 0 ? (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-3">
                    <EmptyState img={ICONS_3D.megaphone} title="No circulars" subtitle="Announcements will show up here." />
                  </motion.div>
                ) : (
                  <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <ListGroup variant="flush" className="scroll-area">
                      {circulars.map((c) => (
                        <ListGroup.Item key={c.id} className="list-soft text-muted">
                          <div className="fw-semibold">{c.title || "Untitled"}</div>
                          <div className="small text-muted">
                            {fmt(toDate(c.createdAt), "DD MMM YYYY")} •{" "}
                            {Array.isArray(c.audience) ? c.audience.join(", ") : c.audience || "All"}
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ===== Styles ===== */}
      <style>{`
/* ===== Modern hero (matches layout) ===== */
.tdb-hero.pro{
  position: relative;
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 18px;
  background:
    linear-gradient(135deg,
      color-mix(in oklab, var(--brand-accent) 18%, transparent) 0%,
      color-mix(in oklab, var(--brand-accent) 10%, transparent) 40%,
      color-mix(in oklab, var(--surface) 96%, transparent) 100%);
  display: grid; grid-template-columns: 1.2fr .8fr; gap: 14px; overflow: hidden;
}
.tdb-hero.pro::after{
  content:""; position:absolute; inset:auto -8% -40% auto; width:48%; height:120%;
  background: radial-gradient(50% 50% at 60% 45%, color-mix(in oklab, #fff 18%, transparent), transparent 60%);
  pointer-events:none;
}
.tdb-hero-left .chip-accent{
  display:inline-flex; align-items:center; gap:.35rem; padding:.25rem .55rem;
  border-radius:.65rem; font-weight:600; font-size:.82rem;
  border: 1px solid color-mix(in oklab, var(--brand-accent) 35%, var(--border));
  background: color-mix(in oklab, var(--brand-accent) 10%, transparent);
  color: var(--brand-accent);
}
.tdb-hero-art{ display:flex; align-items:center; justify-content:flex-end; position:relative; }
.tdb-hero-art img{
  width: 140px; height: 140px; object-fit: contain; border-radius: 18px;
  background: color-mix(in oklab, var(--brand-accent) 10%, transparent);
  border: 1px solid color-mix(in oklab, var(--brand-accent) 35%, var(--border));
  box-shadow: 0 20px 38px color-mix(in oklab, var(--brand-accent) 20%, transparent);
  padding: 8px;
}

/* Inline KPIs (hero) */
.hero-kpis{ display:grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap:10px; margin-top: 4px; }
.hkpi{ border:1px solid var(--border); border-radius: 12px; background: color-mix(in oklab, var(--surface) 98%, transparent); padding:10px 12px; }
.hkpi-label{ font-size:.78rem; color: var(--muted); }
.hkpi-value{ font-weight: 800; font-size: 1.2rem; }
.hkpi-badge{ display:inline-flex; align-items:center; justify-content:center; padding:.2rem .5rem; border-radius:.55rem; font-weight:700; font-size:.85rem; border:1px solid var(--border); background: color-mix(in oklab, var(--surface) 98%, transparent); }
.hkpi-badge.ok   { color: #1e7e34; border-color: color-mix(in oklab, #28a745 25%, var(--border)); background: color-mix(in oklab, #28a745 12%, transparent); }
.hkpi-badge.warn { color: #8a6d00; border-color: color-mix(in oklab, #ffc107 25%, var(--border)); background: color-mix(in oklab, #ffc107 14%, transparent); }

/* Quick actions grid */
.qa-grid{ display:grid; grid-template-columns: repeat(5, minmax(0,1fr)); gap: 10px; }
.qa-tile{
  display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px;
  padding: 12px 10px; border-radius: 14px; width: 100%;
  background: color-mix(in oklab, var(--surface) 98%, transparent);
  border: 1px solid var(--border);
  transition: transform .12s ease, box-shadow .18s ease, border-color .18s ease, background .18s ease;
}
.qa-tile img{
  width: 65px; height:65px; object-fit:contain; border-radius: 12px;
  background: color-mix(in oklab, var(--brand-accent) 9%, transparent);
  border: 1px solid color-mix(in oklab, var(--brand-accent) 30%, var(--border));
  box-shadow: 0 10px 22px color-mix(in oklab, var(--brand-accent) 15%, transparent);
  padding: 6px;
}
.qa-label{ font-weight: 600; font-size: .92rem; color: var(--text); }
.qa-tile:hover{
  transform: translateY(-2px);
  border-color: color-mix(in oklab, var(--brand-accent) 35%, var(--border));
  box-shadow: 0 14px 28px rgba(0,0,0,.12);
  background: color-mix(in oklab, var(--brand-accent) 6%, var(--surface));
}

/* KPI cards hover */
.kpi-card { transition: transform .12s ease, box-shadow .2s ease, border-color .2s ease; }
.kpi-card:hover { transform: translateY(-2px); border-color: color-mix(in oklab, var(--brand-accent) 35%, var(--border)); box-shadow: 0 12px 24px rgba(0,0,0,.12); }

/* Search input in roster header */
.roster-search{ display:flex; align-items:center; gap:.4rem; }
.roster-search .bi{ color: var(--muted); font-size: 1rem; }
.roster-search input{ width: 240px; }

/* Skeleton loader */
.skeleton-block{ display:grid; gap:10px; }
.sk-line{
  height: 14px; border-radius: 8px; background:
  linear-gradient(90deg,
    color-mix(in oklab, var(--surface) 85%, transparent),
    color-mix(in oklab, var(--brand-accent) 10%, transparent),
    color-mix(in oklab, var(--surface) 85%, transparent));
  background-size: 200% 100%;
  animation: skShimmer 1.1s linear infinite;
  border: 1px solid var(--border);
}
@keyframes skShimmer { to { background-position: -200% 0; } }

/* Empty state */
.empty-state{
  display:flex; flex-direction:column; align-items:center; text-align:center;
  border:1px dashed var(--border); border-radius:14px; padding:20px; gap:8px;
  background: color-mix(in oklab, var(--surface) 98%, transparent);
}
.empty-state img{
  width:72px; height:72px; border-radius:14px; object-fit:contain;
  background: color-mix(in oklab, var(--brand-accent) 9%, transparent);
  border: 1px solid color-mix(in oklab, var(--brand-accent) 30%, var(--border));
  box-shadow: 0 10px 22px color-mix(in oklab, var(--brand-accent) 15%, transparent);
  padding:8px;
}
.empty-state .title{ font-weight:700; }
.empty-state .sub{ color: var(--muted); }

/* Base tokens → Bootstrap bridge (for consistency) */
.t-shell{
  --bs-body-bg: var(--bg);
  --bs-body-color: var(--text);
  --bs-border-color: var(--border);
  --bs-secondary-color: var(--muted);
  --bs-primary: var(--brand-accent);
  --bs-link-color: var(--brand-accent);
  --bs-link-hover-color: color-mix(in oklab, var(--brand-accent) 86%, black);
  --bs-focus-ring-color: color-mix(in oklab, var(--brand-accent) 35%, transparent);
  --bs-card-bg: var(--surface);
  --bs-card-color: var(--text);
  --bs-card-border-color: var(--border);
  --bs-table-color: var(--text);
  --bs-table-bg: color-mix(in oklab, var(--surface) 98%, transparent);
  --bs-table-hover-bg: color-mix(in oklab, var(--brand-accent) 8%, transparent);
  --bs-table-border-color: var(--border);
  --bs-list-group-bg: color-mix(in oklab, var(--surface) 98%, transparent);
  --bs-list-group-color: var(--text);
  --bs-list-group-border-color: var(--border);
  --bs-form-control-bg: var(--surface);
  --bs-form-control-color: var(--text);
  --bs-form-control-border-color: var(--border);
}

/* Legacy hero (kept for compatibility if referenced) */
.tdb-hero { border: 1px solid var(--border); border-radius: 16px;
  background:
    radial-gradient(60% 120% at 10% -20%, color-mix(in oklab, var(--brand-accent) 16%, transparent) 0%, transparent 60%),
    radial-gradient(40% 120% at 110% 10%, color-mix(in oklab, var(--brand-accent) 10%, transparent) 0%, transparent 60%),
    color-mix(in oklab, var(--surface) 96%, transparent);
}
.tdb-hero-inner { display:flex; align-items:center; justify-content:space-between; padding: 16px 18px; border-radius: 16px; }
.tdb-eyebrow { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: var(--muted); }

/* Cards/tables */
.soft-card { border-radius: 16px; border: 1px solid var(--border); background: color-mix(in oklab, var(--surface) 98%, transparent); }
.table-soft-hover tbody tr:hover { background: color-mix(in oklab, var(--brand-accent) 7%, transparent); }
.table td, .table th { vertical-align: middle; }

/* Scroll containers with sticky headers */
.scroll-area{ max-height: 360px; overflow: auto; scrollbar-gutter: stable both-edges; }
.scroll-area table thead th{
  position: sticky; top: 0; z-index: 1;
  background: color-mix(in oklab, var(--surface) 98%, transparent);
}

/* Badge contrast for bg-light */
.t-shell .badge.bg-light{
  background-color: var(--bs-table-bg) !important;
  color: var(--text) !important;
  border: 1px solid var(--border);
}

/* Table header contrast */
.t-shell .table thead th{
  background: color-mix(in oklab, var(--surface) 96%, transparent);
  color: var(--text);
  border-bottom-color: var(--border);
}

/* Responsive */
@media (max-width: 1200px){ .qa-grid{ grid-template-columns: repeat(4, minmax(0,1fr)); } }
@media (max-width: 992px){
  .tdb-hero.pro{ grid-template-columns: 1fr; }
  .tdb-hero-art{ justify-content:flex-start; }
  .qa-grid{ grid-template-columns: repeat(3, minmax(0,1fr)); }
}
@media (max-width: 576px){
  .hero-kpis{ grid-template-columns: repeat(2, minmax(0,1fr)); }
  .qa-grid{ grid-template-columns: repeat(2, minmax(0,1fr)); }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce){
  .kpi-card, .qa-tile { transition: none !important; }
}
      `}</style>
    </Container>
  );
}
